import PropTypes from 'prop-types';
import React, { useCallback, useContext, useState } from 'react';
import styled, { css } from 'styled-components';

import AnswerPerformanceView from 'models/location/answer_performance_view';
import BookOpen from 'components/common/icons/nav/book_open';
import ChartLine from 'components/common/icons/nav/chart_line';
import ChevronDownIcon from 'components/lib/icons/chevron_down_icon';
import Cog from 'components/common/icons/nav/cog';
import Columns from 'components/common/icons/nav/columns';
import CommentLines from 'components/common/icons/nav/comment_lines';
import Compass from 'components/common/icons/nav/compass';
import connect from 'components/lib/connect';
import GuidePerformanceView from 'models/location/guide_performance_view';
import Home from 'components/common/icons/nav/home';
import Inbox from 'models/location/inbox';
import KbAdmin from 'models/location/kb_admin';
import Link from 'components/common/link';
import LinkOutStroke from 'components/common/icons/stroke/link-out-stroke';
import List from 'components/common/icons/nav/list';
import Liveboards from 'models/location/liveboards';
import NavigateTo from 'actions/current_location/navigate_to';
import Random from 'components/common/icons/nav/random';
import Reports from 'models/location/reports';
import ShareAlt from 'components/common/icons/nav/share_alt';
import Sitemap from 'components/common/icons/nav/sitemap';
import ThumbsUp from 'components/common/icons/nav/thumbs_up';
import useIsFeatureEnabled from 'components/hooks/use_is_feature_enabled';
import { getLocationUrl } from 'scripts/adapters/routes/location_url';
import { HotkeysShownContext } from 'components/contexts/hotkeys';
import { useExecuteAction } from 'components/hooks/connect_hooks';

export function NavigationMenu({ hasGuideCollections, isSidekickEnabled, onClose }) {
  const isFeatureEnabled = useIsFeatureEnabled();
  const { dispatchHotkeysShown } = useContext(HotkeysShownContext);
  const showHotkeys = useCallback(() => dispatchHotkeysShown('on'), [dispatchHotkeysShown]);

  // satisfies at least one feature
  const satisfiesFeatures = features => {
    if (typeof features === 'string') features = [features];
    return features.some(feature => isFeatureEnabled(feature));
  };

  function renderJourneysLink(opts = {}) {
    if (!isSidekickEnabled) {
      return null;
    }
    if (!satisfiesFeatures(['viewGuidePerformance', 'viewAnswerPerformance', 'answers:admin', 'answers:read'])) {
      return null;
    }
    const linkOpts =
      hasGuideCollections && satisfiesFeatures('viewGuidePerformance')
        ? { dataAid: 'guide-performance', locationModel: GuidePerformanceView }
        : { dataAid: 'answer-performance', locationModel: AnswerPerformanceView };

    return (
      <MenuOption
        data-aid={linkOpts.dataAid}
        icon={opts.icon}
        locationModel={linkOpts.locationModel}
        onClick={onClose}
        title="Journeys"
      />
    );
  }

  if (isFeatureEnabled('newMainNav')) {
    return renderNewNavigation({
      isSidekickEnabled,
      onClose,
      satisfiesFeatures,
      renderJourneysLink,
      showHotkeys,
    });
  }

  function renderSidekickMenuOptions() {
    if (!isSidekickEnabled || !satisfiesFeatures('sidekickConversationManagement')) {
      return null;
    }

    return (
      <>
        <StyledMenuItem data-aid="sidekick-conversations">
          <StyledLink
            className="navigationMenu-option"
            href="/sidekick/communication-sessions"
            onClick={evt => {
              evt.preventDefault();
              window.location.assign('/sidekick/communication-sessions');
            }}
          >
            Gladly AI Conversations
          </StyledLink>
        </StyledMenuItem>
        <StyledMenuItem data-aid="sidekick-reviews">
          <StyledLink
            className="navigationMenu-option"
            href="/sidekick/reviews"
            onClick={evt => {
              evt.preventDefault();
              window.location.assign('/sidekick/reviews');
            }}
          >
            Reviews
          </StyledLink>
        </StyledMenuItem>
        <StyledMenuItem data-aid="sidekick-guides">
          <StyledLink
            className="navigationMenu-option"
            href="/sidekick/guides"
            onClick={evt => {
              evt.preventDefault();
              window.location.assign('/sidekick/guides');
            }}
          >
            Guides
          </StyledLink>
        </StyledMenuItem>
      </>
    );
  }

  return (
    <div className={`navigationMenu navigationMenuV2`}>
      <StyledLine />
      {satisfiesFeatures(['conversation:read']) && (
        <MenuOption locationModel={Inbox} locationPath="" onClick={onClose} title="Home" />
      )}
      {satisfiesFeatures(['answers:admin', 'answers:read']) && (
        <MenuOption locationModel={KbAdmin} onClick={onClose} title="Answers" />
      )}

      {renderJourneysLink()}

      {renderSidekickMenuOptions()}

      {satisfiesFeatures('reports:read') && <MenuOption locationModel={Reports} onClick={onClose} title="Reports" />}
      {satisfiesFeatures('viewLiveboards') && (
        <MenuOption locationModel={Liveboards} onClick={onClose} title="Liveboards" />
      )}
      <StyledLine />
      {satisfiesFeatures(['adminSettings', 'devManagement', 'debuggingView', 'answers:admin', 'topics:admin']) && (
        <StyledMenuItem data-aid="settings-menu">
          <StyledLink
            className="navigationMenu-option"
            href="/admin/home"
            onClick={evt => {
              evt.preventDefault();
              window.location.assign('/admin/home');
            }}
          >
            Settings
          </StyledLink>
        </StyledMenuItem>
      )}
      <StyledLine />
      <StyledMenuItem>
        <StyledExternalLink
          className="navigationMenu-option"
          data-aid="navigationMenu-new-features"
          href="https://help.gladly.com/docs/whats-new"
          tabIndex="-1"
          target="release-notes"
        >
          What's new in Gladly
          <StyledLinkOutStroke />
        </StyledExternalLink>
      </StyledMenuItem>
      <StyledMenuItem>
        <StyledExternalLink
          className="navigationMenu-option"
          data-aid="navigationMenu-help-link"
          href="https://help.gladly.com"
          tabIndex="-1"
          target="help"
        >
          Help Docs
          <StyledLinkOutStroke />
        </StyledExternalLink>
      </StyledMenuItem>
      <StyledMenuItem>
        <StyledExternalLink
          className="navigationMenu-option"
          data-aid="navigationMenu-academy-link"
          href="https://academy.gladly.com"
          tabIndex="-1"
          target="academy"
        >
          Gladly Academy
          <StyledLinkOutStroke />
        </StyledExternalLink>
      </StyledMenuItem>
      {satisfiesFeatures('contactSupport') && (
        <StyledMenuItem data-aid="contact-support-menu">
          <StyledExternalLink
            className="navigationMenu-option"
            data-aid="navigationMenu-help-link"
            href="/support.html"
            tabIndex="-1"
            target="support"
          >
            Contact Support
            <StyledLinkOutStroke />
          </StyledExternalLink>
        </StyledMenuItem>
      )}
      <StyledLine />
      <StyledMenuItem>
        <StyledLink
          className="navigationMenu-option"
          data-aid="navigationMenu-shortcuts-link"
          onClick={showHotkeys}
          tabIndex="-1"
        >
          Keyboard Shortcuts
        </StyledLink>
      </StyledMenuItem>
      <PrivacyPolicySection>
        <PrivacyPolicyLink
          data-aid="navigationMenu-privacy-policy-link"
          href="https://www.gladly.com/privacy-policy/"
          rel="noopener noreferrer"
          tabIndex="-1"
          target="_blank"
        >
          Privacy Policy
        </PrivacyPolicyLink>
      </PrivacyPolicySection>
    </div>
  );
}

NavigationMenu.propTypes = {
  hasGuideCollections: PropTypes.bool.isRequired,
  isSidekickEnabled: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

const NavigationMenuContainer = connect(mapStateToProps)(NavigationMenu);
export default NavigationMenuContainer;

function mapStateToProps({ getProvider }, { onClose }) {
  const organization = getProvider('organization').get();
  // Observe currentLocation so the menu re-renders on navigation. MenuOption's
  // active-state check reads window.location.pathname; without this
  // subscription the connect HOC's binding-observation can skip re-rendering
  // when the agent navigates, leaving the active highlight stale.
  const currentLocation = getProvider('currentLocation').get();
  return {
    onClose,
    hasGuideCollections: getProvider('guideCollections').findAll().length > 0,
    isSidekickEnabled: !!organization?.isSidekickEnabled,
    currentLocation,
  };
}

function renderNewNavigation({ isSidekickEnabled, onClose, satisfiesFeatures, renderJourneysLink, showHotkeys }) {
  const isExternalActive = path => window.location.pathname.startsWith(path);

  const goExternal = path => evt => {
    evt.preventDefault();
    window.location.assign(path);
  };

  const journeyItem = renderJourneysLink({ icon: Random });
  const showAnswers = satisfiesFeatures(['answers:admin', 'answers:read']);
  const showSidekickItems = isSidekickEnabled && satisfiesFeatures('sidekickConversationManagement');
  const showLiveboards = satisfiesFeatures('viewLiveboards');
  const showReports = satisfiesFeatures('reports:read');
  const showSettings = satisfiesFeatures([
    'adminSettings',
    'devManagement',
    'debuggingView',
    'answers:admin',
    'topics:admin',
  ]);
  const hasManage = showAnswers || showSidekickItems;
  const hasAnalyze = !!journeyItem || showLiveboards || showSidekickItems || showReports;
  const hasDeploy = isSidekickEnabled;

  return (
    <NewNavRoot aria-label="Main navigation" className="navigationMenu navigationMenuV2">
      <StyledLine />
      <NewItemList>
        {satisfiesFeatures(['conversation:read']) && (
          <li>
            <MenuOption icon={Home} locationModel={Inbox} locationPath="" onClick={onClose} title="Home" />
          </li>
        )}
      </NewItemList>

      {hasManage && (
        <>
          <SectionHeading id="nav-section-manage">Manage</SectionHeading>
          <NewItemList aria-labelledby="nav-section-manage">
            {showAnswers && (
              <li>
                <MenuOption icon={BookOpen} locationModel={KbAdmin} onClick={onClose} title="Answers" />
              </li>
            )}
            {showSidekickItems && (
              <li>
                <ExternalNavItem
                  dataAid="sidekick-conversations"
                  href="/sidekick/communication-sessions"
                  icon={CommentLines}
                  isActive={isExternalActive('/sidekick/communication-sessions')}
                  label="AI Conversations"
                  onClick={goExternal('/sidekick/communication-sessions')}
                />
              </li>
            )}
            {showSidekickItems && (
              <li>
                <ExternalNavItem
                  dataAid="sidekick-guides"
                  href="/sidekick/guides"
                  icon={Compass}
                  isActive={isExternalActive('/sidekick/guides')}
                  label="Guides"
                  onClick={goExternal('/sidekick/guides')}
                />
              </li>
            )}
            {showSidekickItems && (
              <li>
                <ExternalNavItem
                  dataAid="sidekick-workflows"
                  href="/sidekick/workflows"
                  icon={Sitemap}
                  isActive={isExternalActive('/sidekick/workflows')}
                  label="Workflows"
                  onClick={goExternal('/sidekick/workflows')}
                />
              </li>
            )}
          </NewItemList>
        </>
      )}

      {hasAnalyze && (
        <>
          <SectionHeading id="nav-section-analyze">Analyze</SectionHeading>
          <NewItemList aria-labelledby="nav-section-analyze">
            {journeyItem && <li>{journeyItem}</li>}
            {showLiveboards && (
              <li>
                <MenuOption icon={Columns} locationModel={Liveboards} onClick={onClose} title="Liveboards" />
              </li>
            )}
            {showSidekickItems && (
              <li>
                <ExternalNavItem
                  dataAid="sidekick-reviews"
                  href="/sidekick/reviews"
                  icon={ThumbsUp}
                  isActive={isExternalActive('/sidekick/reviews')}
                  label="Feedback"
                  onClick={goExternal('/sidekick/reviews')}
                />
              </li>
            )}
            {showReports && (
              <li>
                <MenuOption icon={ChartLine} locationModel={Reports} onClick={onClose} title="Reports" />
              </li>
            )}
          </NewItemList>
        </>
      )}

      {hasDeploy && (
        <>
          <SectionHeading id="nav-section-deploy">Deploy</SectionHeading>
          <NewItemList aria-labelledby="nav-section-deploy">
            <li>
              <ExternalNavItem
                dataAid="sidekick-changelog"
                href="/sidekick/audit-history"
                icon={List}
                isActive={isExternalActive('/sidekick/audit-history')}
                label="Change log"
                onClick={goExternal('/sidekick/audit-history')}
              />
            </li>
            <li>
              <ExternalNavItem
                dataAid="sidekick-entry-points"
                href="/sidekick/entry-points"
                icon={ShareAlt}
                isActive={isExternalActive('/sidekick/entry-points')}
                label="Entry points"
                onClick={goExternal('/sidekick/entry-points')}
              />
            </li>
          </NewItemList>
        </>
      )}

      {showSettings && (
        <>
          <SectionHeading id="nav-section-admin">Admin</SectionHeading>
          <NewItemList aria-labelledby="nav-section-admin">
            <li>
              <ExternalNavItem
                dataAid="settings-menu"
                href="/admin/home"
                icon={Cog}
                isActive={isExternalActive('/admin')}
                label="Settings"
                onClick={goExternal('/admin/home')}
              />
            </li>
          </NewItemList>
        </>
      )}
      <ResourcesSection showContactSupport={satisfiesFeatures('contactSupport')} />
      <KeyboardShortcutsList>
        <li>
          <StyledMenuItem>
            <StyledLink
              className="navigationMenu-option"
              data-aid="navigationMenu-shortcuts-link"
              onClick={showHotkeys}
              tabIndex="-1"
            >
              Keyboard Shortcuts
            </StyledLink>
          </StyledMenuItem>
        </li>
      </KeyboardShortcutsList>
      <NewPrivacyPolicySection>
        <PrivacyPolicyLink
          data-aid="navigationMenu-privacy-policy-link"
          href="https://www.gladly.com/privacy-policy/"
          rel="noopener noreferrer"
          tabIndex="-1"
          target="_blank"
        >
          Privacy Policy
          <VisuallyHidden> (opens in new tab)</VisuallyHidden>
        </PrivacyPolicyLink>
      </NewPrivacyPolicySection>
    </NewNavRoot>
  );
}

const NavIcon = ({ icon: IconComponent }) => (
  <NavIconBox aria-hidden="true">
    <IconComponent />
  </NavIconBox>
);

NavIcon.propTypes = {
  icon: PropTypes.elementType.isRequired,
};

const ExternalNavItem = ({ dataAid, href, icon, isActive, label, onClick }) => (
  <StyledMenuItem data-aid={dataAid} isActive={isActive}>
    <StyledLink
      aria-current={isActive ? 'page' : undefined}
      className="navigationMenu-option"
      href={href}
      onClick={onClick}
    >
      <NavIcon icon={icon} />
      {label}
    </StyledLink>
  </StyledMenuItem>
);

ExternalNavItem.propTypes = {
  dataAid: PropTypes.string,
  href: PropTypes.string.isRequired,
  icon: PropTypes.elementType.isRequired,
  isActive: PropTypes.bool,
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
};

export const MenuOption = ({ icon, locationModel, onClick, title }) => {
  const executeAction = useExecuteAction();
  const handleClick = useCallback(
    evt => {
      evt.preventDefault();
      executeAction(NavigateTo, locationModel.create());
      onClick();
    },
    [executeAction, locationModel, onClick]
  );

  // Some location models (e.g. GuidePerformanceView) default query params, so
  // strip the query before segment comparison — otherwise the first path
  // segment would include "?..." and never match the bare pathname segment.
  const menuPath = getLocationUrl(locationModel.create()).split('?')[0];
  const pathname = window.location.pathname;
  // Director.js maps the root path "/" to the home action without rewriting the URL.
  // Treat "/" as "/home" so the Home menu item highlights when the agent lands on home.
  const isAtRoot = pathname === '/' || pathname === '';
  const currentLocation = isAtRoot ? 'home' : pathname.split('/')[1];
  const isActiveItem = menuPath.split('/')[1] === currentLocation;
  return (
    <StyledMenuItem isActive={isActiveItem}>
      <StyledLink
        aria-current={isActiveItem ? 'page' : undefined}
        className="navigationMenu-option"
        href={menuPath}
        onClick={handleClick}
      >
        {icon && <NavIcon icon={icon} />}
        {title}
      </StyledLink>
    </StyledMenuItem>
  );
};

MenuOption.propTypes = {
  icon: PropTypes.elementType,
  locationModel: PropTypes.func.isRequired,
  onClick: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
};

const ResourcesSection = ({ showContactSupport }) => {
  const [isOpen, setIsOpen] = useState(false);
  const headingId = 'nav-section-resources';
  const listId = 'nav-section-resources-list';
  return (
    <>
      <SectionToggle
        aria-controls={listId}
        aria-expanded={isOpen}
        data-aid="navigationMenu-resources-toggle"
        id={headingId}
        onClick={() => setIsOpen(open => !open)}
        type="button"
      >
        Resources
        <SectionToggleChevron $isOpen={isOpen} aria-hidden="true">
          <ChevronDownIcon />
        </SectionToggleChevron>
      </SectionToggle>
      {isOpen && (
        <NewItemList aria-labelledby={headingId} id={listId}>
          <li>
            <StyledMenuItem>
              <StyledExternalLink
                className="navigationMenu-option"
                data-aid="navigationMenu-new-features"
                href="https://help.gladly.com/docs/whats-new"
                tabIndex="-1"
                target="release-notes"
              >
                What's new in Gladly
                <StyledLinkOutStroke aria-hidden="true" />
                <VisuallyHidden>(opens in new tab)</VisuallyHidden>
              </StyledExternalLink>
            </StyledMenuItem>
          </li>
          <li>
            <StyledMenuItem>
              <StyledExternalLink
                className="navigationMenu-option"
                data-aid="navigationMenu-help-link"
                href="https://help.gladly.com"
                tabIndex="-1"
                target="help"
              >
                Help Docs
                <StyledLinkOutStroke aria-hidden="true" />
                <VisuallyHidden>(opens in new tab)</VisuallyHidden>
              </StyledExternalLink>
            </StyledMenuItem>
          </li>
          <li>
            <StyledMenuItem>
              <StyledExternalLink
                className="navigationMenu-option"
                data-aid="navigationMenu-academy-link"
                href="https://academy.gladly.com"
                tabIndex="-1"
                target="academy"
              >
                Gladly Academy
                <StyledLinkOutStroke aria-hidden="true" />
                <VisuallyHidden>(opens in new tab)</VisuallyHidden>
              </StyledExternalLink>
            </StyledMenuItem>
          </li>
          {showContactSupport && (
            <li>
              <StyledMenuItem data-aid="contact-support-menu">
                <StyledExternalLink
                  className="navigationMenu-option"
                  data-aid="navigationMenu-help-link"
                  href="/support.html"
                  tabIndex="-1"
                  target="support"
                >
                  Contact Support
                  <StyledLinkOutStroke aria-hidden="true" />
                  <VisuallyHidden>(opens in new tab)</VisuallyHidden>
                </StyledExternalLink>
              </StyledMenuItem>
            </li>
          )}
        </NewItemList>
      )}
    </>
  );
};

ResourcesSection.propTypes = {
  showContactSupport: PropTypes.bool.isRequired,
};

const ActiveItem = css`
  background-color: ${p => p.theme.colors.gray200};
  font-weight: bold;
  &:before {
    background-color: ${p => p.theme.colors.green500};
    bottom: 0;
    content: '';
    left: 0;
    margin: auto;
    position: absolute;
    top: 0;
    width: 4px;
  }
`;

const StyledLinkOutStroke = styled(LinkOutStroke)`
  height: 14px;
  width: 14px;
  margin-left: 4px;
`;

const NavIconBox = styled.span`
  align-items: center;
  color: ${p => p.theme.colors.gray800};
  display: inline-flex;
  flex-shrink: 0;
  height: 24px;
  justify-content: center;
  margin-right: 4px;
  width: 27px;

  svg {
    fill: currentColor;
    height: 16px;
    width: 16px;

    path {
      fill: currentColor;
    }
  }
`;

// Flex column so the privacy policy can use margin-top: auto to anchor at the
// bottom of the menu when content is short, while still flowing in-line
// (and scrolling) when content is long. Avoids the legacy
// position: absolute approach where privacy policy floats over the items.
const NewNavRoot = styled.nav`
  display: flex;
  flex-direction: column;
  min-height: 100%;
`;

const NewItemList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;

  &:empty {
    display: none;
  }
`;

const SectionHeading = styled.h2`
  color: ${p => p.theme.colors.gray800};
  font-size: ${p => p.theme.fontSize.base};
  font-weight: bold;
  margin: 0;
  padding: 12px 24px 4px;
  text-transform: none;
`;

// Resources collapse/expand toggle. Headed as <h2> visually but rendered as a
// <button> inside it for keyboard a11y. We use a button element directly
// (instead of nesting <button> inside <h2>) because the toggle is the only
// labelable thing here and screen readers already announce the heading via
// aria-labelledby on the controlled list.
const SectionToggle = styled.button`
  align-items: center;
  background: none;
  border: 0;
  color: ${p => p.theme.colors.gray800};
  cursor: pointer;
  display: inline-flex;
  font-family: inherit;
  font-size: ${p => p.theme.fontSize.base};
  font-weight: bold;
  margin: 12px 0 0;
  padding: 8px 24px;
  text-align: left;

  &:hover {
    background-color: ${p => p.theme.colors.green100};
  }
`;

const SectionToggleChevron = styled.span`
  align-items: center;
  color: ${p => p.theme.colors.gray800};
  display: inline-flex;
  height: 12px;
  justify-content: center;
  margin-left: 8px;
  transform: rotate(${p => (p.$isOpen ? '0deg' : '-90deg')});
  transition: transform 0.15s ease;
  width: 12px;

  svg {
    fill: currentColor;
    height: 12px;
    width: 12px;

    path {
      fill: currentColor;
    }
  }
`;

// Keyboard Shortcuts is the first of the two bottom-anchored items
// (Keyboard Shortcuts + Privacy Policy). margin-top: auto inside the
// flex-column NewNavRoot pushes both to the bottom when content is short,
// while still flowing in-line (and scrolling) when content is long.
// Color is overridden to gray800 to match the Privacy Policy footer link.
const KeyboardShortcutsList = styled(NewItemList)`
  margin-top: auto;

  a,
  a:hover {
    color: ${p => p.theme.colors.gray800};
  }
`;

// Privacy policy sits directly under Keyboard Shortcuts (which has
// margin-top: auto), so the pair anchors to the bottom of the menu when
// content is short and falls back to in-flow scrolling when it's not.
const NewPrivacyPolicySection = styled.div`
  padding: 8px 0;
`;

const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

export const StyledLink = styled(Link)`
  color: ${p => p.theme.colors.black};
  align-items: center;
  display: flex;
  height: 32px;
  padding: 6px 24px;
  cursor: pointer;
  &:hover {
    color: ${p => p.theme.colors.black};
    text-decoration: none;
  }
`;

const StyledLine = styled.div`
  height: 1px;
  background-color: ${p => p.theme.colors.gray300};
  margin: 2px 0;

  & + & {
    display: none;
  }
`;

const StyledMenuItem = styled.div`
  cursor: pointer;
  padding: 0;
  position: relative;
  &:hover {
    background-color: ${p => p.theme.colors.green100};
  }
  ${p => p.isActive && ActiveItem}
`;

export const StyledExternalLink = styled.a`
  color: ${p => p.theme.colors.black};
  cursor: pointer;
  &:hover {
    color: ${p => p.theme.colors.green400};
    text-decoration: none;
  }
`;

const PrivacyPolicySection = styled.div`
  bottom: 10px;
  left: 0;
  width: 100%;
  padding: 8px 0;
  position: absolute;
`;

const PrivacyPolicyLink = styled.a`
  color: ${p => p.theme.colors.gray800};
  display: block;
  width: 100%;
  height: 32px;
  padding: 6px 24px;
  cursor: pointer;
  &:hover {
    color: ${p => p.theme.colors.gray800};
    text-decoration: none;
    background-color: ${p => p.theme.colors.green100};
  }
`;
